import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "@phosphor-icons/react";
import { Header } from "@/components/site/Header";
import { ServiceArea } from "@/components/site/ServiceArea";
import { Footer } from "@/components/site/Footer";
import { Seo } from "@/components/site/Seo";
import { Reveal } from "@/components/site/Reveal";
import { getGuide, GUIDES } from "@/data/guides";

const SITE = "https://treasurevalleyturf.com";

export default function GuidePage() {
    const { slug } = useParams();
    const guide = getGuide(slug);

    if (!guide) return <Navigate to="/guides" replace />;

    const jsonLd = [
        {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: guide.title,
            description: guide.description,
            datePublished: guide.date,
            author: { "@id": `${SITE}/#business` },
            publisher: { "@id": `${SITE}/#business` },
            mainEntityOfPage: `${SITE}/guides/${guide.slug}`,
        },
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
                { "@type": "ListItem", position: 2, name: "Guides", item: `${SITE}/guides` },
                { "@type": "ListItem", position: 3, name: guide.title, item: `${SITE}/guides/${guide.slug}` },
            ],
        },
        ...(guide.faq
            ? [{
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: guide.faq.map((f) => ({
                    "@type": "Question",
                    name: f.q,
                    acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
            }]
            : []),
    ];

    return (
        <div data-testid="guide-page" className="relative min-h-screen bg-white text-brand-text">
            <Seo
                title={`${guide.title} | Treasure Valley Turf & Greens`}
                description={guide.description}
                canonical={`${SITE}/guides/${guide.slug}`}
                jsonLd={jsonLd}
            />
            <ServiceArea />
            <Header />

            <main>
                <article className="mx-auto max-w-3xl px-6 pb-16 pt-44 md:pt-56">
                    <Reveal>
                        <Link to="/guides" data-testid="guide-back-link" className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-brand-muted transition-colors hover:text-brand-accent">
                            <ArrowLeft size={16} weight="bold" /> All guides
                        </Link>
                    </Reveal>
                    <Reveal delay={0.06}>
                        <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.18em] text-brand-muted">
                            <span className="rounded-full bg-brand-accent/10 px-3 py-1 text-brand-accent">{guide.category}</span>
                            <span>{guide.readTime}</span>
                            <span>·</span>
                            <time dateTime={guide.date}>
                                {new Date(guide.date + "T12:00:00").toLocaleDateString("en-US", { month: "long", year: "numeric" })}
                            </time>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <h1 className="font-display mt-6 text-balance text-3xl font-semibold leading-[1.08] tracking-tight md:text-5xl">
                            {guide.title}
                        </h1>
                    </Reveal>
                    <Reveal delay={0.18}>
                        <p className="mt-6 text-lg leading-relaxed text-brand-muted">{guide.intro}</p>
                    </Reveal>

                    <div className="mt-12 space-y-12">
                        {guide.sections.map((s, i) => (
                            <Reveal key={s.heading} delay={0.05}>
                                <section data-testid={`guide-section-${i}`}>
                                    <h2 className="font-display text-2xl font-semibold tracking-tight md:text-3xl">{s.heading}</h2>
                                    {s.paragraphs.map((p, j) => (
                                        <p key={j} className="mt-4 text-base leading-relaxed text-brand-muted">{p}</p>
                                    ))}
                                </section>
                            </Reveal>
                        ))}
                    </div>

                    {guide.faq && (
                        <Reveal>
                            <section className="mt-14 rounded-3xl border border-brand-border bg-brand-surface/50 p-7 md:p-9">
                                <h2 className="font-display text-2xl font-semibold tracking-tight">Quick answers</h2>
                                <div className="mt-6 space-y-6">
                                    {guide.faq.map((f, i) => (
                                        <div key={f.q} data-testid={`guide-faq-${i}`}>
                                            <h3 className="font-display text-lg font-semibold">{f.q}</h3>
                                            <p className="mt-1.5 text-sm leading-relaxed text-brand-muted md:text-base">{f.a}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        </Reveal>
                    )}

                    <Reveal>
                        <div className="mt-14 flex flex-col items-center gap-4 rounded-3xl border border-brand-accent/30 bg-brand-accent/5 p-8 text-center">
                            <p className="font-display text-2xl font-medium">Want an exact number for your yard?</p>
                            <p className="max-w-md text-sm text-brand-muted">
                                Free on-site measurement and a flat, itemized quote — about 20 minutes, zero obligation.
                            </p>
                            <a href="/#quote" data-testid="guide-cta-quote" className="group inline-flex items-center gap-2 rounded-full bg-brand-accent px-7 py-4 text-base font-bold text-white transition-all duration-300 hover:bg-brand-accentHover">
                                Get your free quote
                                <ArrowRight size={18} weight="bold" className="transition-transform group-hover:translate-x-1" />
                            </a>
                        </div>
                    </Reveal>
                </article>

                {/* More guides */}
                <section className="border-t border-brand-border/60 bg-brand-surface/50 py-16">
                    <div className="mx-auto max-w-3xl px-6">
                        <Reveal>
                            <h2 className="font-display text-2xl font-medium tracking-tight">Keep reading</h2>
                        </Reveal>
                        <div className="mt-6 space-y-3">
                            {GUIDES.filter((g) => g.slug !== guide.slug).map((g) => (
                                <Reveal key={g.slug} delay={0.05}>
                                    <Link
                                        to={`/guides/${g.slug}`}
                                        data-testid={`guide-more-${g.slug}`}
                                        className="group flex items-center justify-between gap-4 rounded-2xl border border-brand-border bg-white p-5 transition-colors hover:border-brand-accent/40"
                                    >
                                        <span className="font-display text-base font-medium transition-colors group-hover:text-brand-accent md:text-lg">
                                            {g.title}
                                        </span>
                                        <ArrowRight size={18} weight="bold" className="shrink-0 text-brand-accent transition-transform group-hover:translate-x-1" />
                                    </Link>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}
